import clsx from 'clsx'
import { forwardRef, useState, useRef, useEffect } from 'react'

import styles from './Select.module.scss'

export interface SelectOption {
  label: string
  value: string
}

export interface SelectProps<T extends Record<string, any>> {
  options: T[]
  value?: string
  onChange?: (event: { detail: { value: string } }) => void
  getOptionValue?: (option: T) => string
  getOptionLabel?: (option: T) => string
  isOptionDisabled?: (option: T) => boolean
  placeholder?: string
  className?: string
  'data-testid'?: string
}

export const Select = forwardRef(function Select<T extends Record<string, any>>(
  {
    options,
    value,
    onChange,
    getOptionValue = option => option.value,
    getOptionLabel = option => option.label,
    isOptionDisabled = () => false,
    placeholder = 'Select',
    className,
    'data-testid': dataTestId,
  }: SelectProps<T>,
) {
  const [open, setOpen] = useState(false)
  const [openUp, setOpenUp] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(opt => getOptionValue(opt) === value)

  const handleSelect = (val: string) => {
    onChange?.({ detail: { value: val } })
    setOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      setOpenUp(spaceBelow < 260 && spaceAbove > spaceBelow)
    }
  }, [open])

  return (
    <div ref={containerRef} className={clsx(styles.selectContainer, className)} data-testid={dataTestId}>
      <div className={clsx(styles.selectControl, open && styles.open)} onClick={() => setOpen(prev => !prev)}>
        <span className={styles.selectedValue}>{selectedOption ? getOptionLabel(selectedOption) : placeholder}</span>

        <span className={clsx(styles.arrow, open && styles.rotate)} />
      </div>

      {open && (
        <div className={clsx(styles.dropdown, openUp && styles.dropdownTop)}>
          <div className={styles.scroll}>
            {options.map(option => {
              const optionValue = getOptionValue(option)
              const isActive = optionValue === value

              return (
                <div
                  key={optionValue}
                  className={clsx(
                    styles.option,
                    isActive && styles.active,
                    isOptionDisabled(option) && styles.disabled,
                  )}
                  onClick={() => !isOptionDisabled(option) && handleSelect(optionValue)}
                  onMouseEnter={e => {
                    const el = e.currentTarget
                    if (el.scrollWidth > el.clientWidth) {
                      const rect = el.getBoundingClientRect()
                      const tooltip = document.createElement('div')
                      tooltip.className = styles.tooltip
                      tooltip.innerText = el.innerText

                      tooltip.style.top = `${rect.top}px`
                      tooltip.style.left = `${rect.left - 8}px`
                      tooltip.style.transform = 'translate(-100%, 0)'

                      document.body.appendChild(tooltip)
                      el.dataset.tooltipId = 'true'
                    }
                  }}
                  onMouseLeave={() => {
                    document.querySelectorAll(`.${styles.tooltip}`).forEach(el => el.remove())
                  }}
                >
                  {getOptionLabel(option)}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}) as <T extends Record<string, any>>(props: SelectProps<T>) => JSX.Element
